import React, { useState } from "react";
import useSWR from "swr";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { PlanAllowances } from "@/components/account/plan-allowances";
import { useAuth } from "@/context/AuthContext";
import { useOrganization } from "@/context/OrganizationContext";
import { BillingService } from "@/services/billing.service";
import { getSubscription, cancelSubscription } from "@/services/subscription.service";
import { listPublicPlans, subscribeToPlan, type Plan } from "@/services/plans.service";
import { getInvoices } from "@/services/invoice.service";
import { SupportService, type TicketCategory, type TicketPriority, type TicketStatus } from "@/services/support.service";
import { useTickets, useTicket } from "@/hooks/use-support";
import { ExternalLink, Loader2, Plus, ArrowLeft, Clock, Send, Ticket, Download, FileText, ChevronLeft, ChevronRight, CreditCard, AlertTriangle } from "lucide-react";

// ─── Invoice status colours ───────────────────────────────────────────────────

const invoiceStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
  switch (status) {
    case "paid": return "default";
    case "open": return "secondary";
    case "void":
    case "uncollectible": return "destructive";
    default: return "outline";
  }
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const statusColors: Record<TicketStatus, string> = {
  open: "bg-blue-500",
  in_progress: "bg-yellow-500",
  resolved: "bg-green-500",
  closed: "bg-gray-500",
};

const priorityColors: Record<TicketPriority, string> = {
  urgent: "text-red-600 bg-red-100",
  high: "text-orange-600 bg-orange-100",
  medium: "text-yellow-600 bg-yellow-100",
  low: "text-green-600 bg-green-100",
};

// ─── Sub-views ────────────────────────────────────────────────────────────────

interface TicketDetailViewProps {
  ticketId: string;
  onBack: () => void;
}

const TicketDetailView: React.FC<TicketDetailViewProps> = ({ ticketId, onBack }) => {
  const { ticket, isLoading, mutate } = useTicket(ticketId);
  const [replyContent, setReplyContent] = useState("");
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!replyContent.trim()) return;
    setSending(true);
    try {
      await SupportService.addMessage(ticketId, replyContent.trim());
      setReplyContent("");
      mutate();
    } catch {
      // ignore for now
    } finally {
      setSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!ticket) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <span className="text-sm text-muted-foreground">Ticket #{ticket.ticketNumber}</span>
      </div>

      <div className="p-4 border rounded-lg space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-base">{ticket.subject}</h3>
          <Badge className={priorityColors[ticket.priority]}>{ticket.priority}</Badge>
        </div>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${statusColors[ticket.status]}`} />
            <span className="capitalize">{ticket.status.replace("_", " ")}</span>
          </div>
          <span>·</span>
          <span className="capitalize">{ticket.category.replace("_", " ")}</span>
          <span>·</span>
          <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
        </div>
      </div>

      {/* Messages */}
      <div className="space-y-3">
        {ticket.messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex gap-3 p-3 rounded-lg text-sm ${
              msg.sender === "admin" ? "bg-blue-50" : "bg-gray-50"
            }`}
          >
            <Avatar className="h-7 w-7 shrink-0">
              <AvatarFallback className="text-xs">{msg.senderName.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{msg.senderName}</span>
                {msg.sender === "admin" && (
                  <span className="text-xs text-blue-600">(Support)</span>
                )}
                <span className="text-xs text-muted-foreground ml-auto flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {new Date(msg.createdAt).toLocaleString()}
                </span>
              </div>
              <p className="whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
        {ticket.messages.length === 0 && (
          <p className="text-sm text-muted-foreground py-4 text-center">No messages yet.</p>
        )}
      </div>

      {/* Reply — only for open/in_progress tickets */}
      {(ticket.status === "open" || ticket.status === "in_progress") && (
        <div className="space-y-2 pt-2 border-t">
          <Label>Reply</Label>
          <Textarea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder="Type your reply..."
            rows={3}
          />
          <div className="flex justify-end">
            <Button onClick={handleSend} disabled={!replyContent.trim() || sending} size="sm">
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Send
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Ticket List View ────────────────────────────────────────────────────────

interface TicketListViewProps {
  onSelectTicket: (id: string) => void;
  onCreateNew: () => void;
}

const TicketListView: React.FC<TicketListViewProps> = ({ onSelectTicket, onCreateNew }) => {
  const { tickets, isLoading } = useTickets(1, 20);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="text-center py-12 space-y-4">
        <Ticket className="h-10 w-10 mx-auto text-muted-foreground" />
        <div>
          <p className="font-medium">No support tickets yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Create a ticket and our team will get back to you.
          </p>
        </div>
        <Button onClick={onCreateNew}>
          <Plus className="mr-2 h-4 w-4" />
          Create Ticket
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={onCreateNew} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          New Ticket
        </Button>
      </div>
      <div className="space-y-2">
        {tickets.map((ticket) => (
          <button
            key={ticket.id}
            className="w-full text-left p-4 border rounded-lg hover:bg-muted/50 transition-colors space-y-2"
            onClick={() => onSelectTicket(ticket.id)}
          >
            <div className="flex items-start justify-between gap-2">
              <span className="font-medium text-sm">{ticket.subject}</span>
              <Badge className={`${priorityColors[ticket.priority]} shrink-0 text-xs`}>
                {ticket.priority}
              </Badge>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <div className={`w-1.5 h-1.5 rounded-full ${statusColors[ticket.status]}`} />
                <span className="capitalize">{ticket.status.replace("_", " ")}</span>
              </div>
              <span>#{ticket.ticketNumber}</span>
              <span>·</span>
              <span className="capitalize">{ticket.category.replace("_", " ")}</span>
              <span>·</span>
              <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
              <span className="ml-auto">{ticket.messageCount} message{ticket.messageCount !== 1 ? "s" : ""}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Create Ticket Form ───────────────────────────────────────────────────────

interface CreateTicketFormProps {
  onCancel: () => void;
  onCreated: () => void;
}

const CreateTicketForm: React.FC<CreateTicketFormProps> = ({ onCancel, onCreated }) => {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<TicketCategory>("general");
  const [priority, setPriority] = useState<TicketPriority>("medium");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) {
      setError("Subject and description are required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await SupportService.createTicket({ subject: subject.trim(), description: description.trim(), category, priority });
      onCreated();
    } catch (err: any) {
      setError(err.message || "Failed to create ticket");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <span className="font-medium">New Ticket</span>
      </div>

      <div className="space-y-2">
        <Label htmlFor="ticket-subject">Subject</Label>
        <Input
          id="ticket-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Brief summary of your issue"
          maxLength={255}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="ticket-desc">Description</Label>
        <Textarea
          id="ticket-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe your issue in detail..."
          rows={5}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Category</Label>
          <Select value={category} onValueChange={(v) => setCategory(v as TicketCategory)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="general">General</SelectItem>
              <SelectItem value="billing">Billing</SelectItem>
              <SelectItem value="technical">Technical</SelectItem>
              <SelectItem value="feature_request">Feature Request</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Priority</Label>
          <Select value={priority} onValueChange={(v) => setPriority(v as TicketPriority)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex gap-2 justify-end pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
          Submit Ticket
        </Button>
      </div>
    </form>
  );
};

// ─── Main AccountPage ─────────────────────────────────────────────────────────

type SupportView = "list" | "create" | { ticketId: string };

const AccountPage: React.FC = () => {
  const { user } = useAuth();
  const { can } = useOrganization();
  // Billing/Invoices and Support tabs are hidden (and their data never
  // fetched) for members without the matching feature permission.
  const canBilling = can("billing");
  const canSupport = can("support");
  const [payingInvoiceId, setPayingInvoiceId] = useState<string | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const [supportView, setSupportView] = useState<SupportView>("list");
  const [invoicePage, setInvoicePage] = useState(1);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [subscribeError, setSubscribeError] = useState<string | null>(null);

  const { data: subscription, isLoading: subLoading, mutate: refetchSubscription } = useSWR(
    canBilling ? "subscription" : null,
    () => getSubscription(),
  );

  const { data: invoicesData, isLoading: invoicesLoading } = useSWR(
    canBilling ? ["invoices", invoicePage] : null,
    () => getInvoices(invoicePage, 10),
  );

  const { data: plans, isLoading: plansLoading } = useSWR(
    canBilling && subscription === null ? "public-plans" : null,
    listPublicPlans,
  );

  const handlePayInvoice = async (invoiceId: string) => {
    setPayingInvoiceId(invoiceId);
    setPayError(null);
    try {
      const { checkoutUrl, alreadyPaid } = await BillingService.getPaymentLink(invoiceId);
      if (alreadyPaid) return;
      if (checkoutUrl) window.open(checkoutUrl, "_blank");
    } catch (error: any) {
      setPayError(error.message || "Failed to create payment link");
    } finally {
      setPayingInvoiceId(null);
    }
  };

  const handleSubscribe = async (planId: string) => {
    setSubscribing(planId);
    setSubscribeError(null);
    try {
      const { setupUrl } = await subscribeToPlan(planId);
      if (setupUrl) window.open(setupUrl, "_blank");
      refetchSubscription();
    } catch (error: any) {
      setSubscribeError(error.message || "Failed to subscribe");
    } finally {
      setSubscribing(null);
    }
  };

  const handleCancelSubscription = async () => {
    setCanceling(true);
    setCancelError(null);
    try {
      await cancelSubscription();
      setCancelConfirm(false);
      refetchSubscription();
    } catch (error: any) {
      setCancelError(error.message || "Failed to cancel subscription");
    } finally {
      setCanceling(false);
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  return (
    <div className="flex-1 space-y-6 p-2">
      <div className="space-y-2">
        <h2 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
          My Account
        </h2>
        <p className="text-muted-foreground">
          Manage your account settings, billing, and support.
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6" onValueChange={() => setSupportView("list")}>
        <TabsList
          className={`grid w-full ${
            ["", "grid-cols-1", "grid-cols-2", "grid-cols-3", "grid-cols-4"][
              1 + (canBilling ? 2 : 0) + (canSupport ? 1 : 0)
            ]
          }`}
        >
          <TabsTrigger value="profile">Profile Details</TabsTrigger>
          {canBilling && <TabsTrigger value="billing">Billing & Package</TabsTrigger>}
          {canBilling && <TabsTrigger value="invoicing">Invoices</TabsTrigger>}
          {canSupport && <TabsTrigger value="support">Support</TabsTrigger>}
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>
                Update your personal information and email preferences.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" value={user?.email || ""} disabled />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="clientId">Client ID</Label>
                  <Input id="clientId" value={user?.clientId || ""} disabled />
                </div>
              </div>
              <div className="flex justify-end">
                <Button>Save Changes</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="billing" className="space-y-6">
          {/* Plan Details */}
          <Card>
            <CardHeader>
              <CardTitle>Current Plan</CardTitle>
              <CardDescription>Your active subscription and plan details.</CardDescription>
            </CardHeader>
            <CardContent>
              {subLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-6 w-40" />
                  <Skeleton className="h-4 w-64" />
                  <Skeleton className="h-4 w-48" />
                </div>
              ) : !subscription ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">Choose a plan to get started.</p>
                  {plansLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-24 w-full" />
                      <Skeleton className="h-24 w-full" />
                    </div>
                  ) : !plans || plans.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No plans available. Contact support to get started.</p>
                  ) : (
                    <>
                      {subscribeError && (
                        <p className="text-sm text-destructive">{subscribeError}</p>
                      )}
                      <div className="grid gap-3 sm:grid-cols-2">
                        {plans.map((plan: Plan) => (
                          <div key={plan.id} className="p-4 border rounded-lg space-y-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="font-semibold">{plan.name}</p>
                                {plan.description && (
                                  <p className="text-xs text-muted-foreground mt-0.5">{plan.description}</p>
                                )}
                              </div>
                              <div className="text-right shrink-0">
                                <p className="font-bold">
                                  {new Intl.NumberFormat("en-US", {
                                    style: "currency",
                                    currency: plan.currency.toUpperCase(),
                                  }).format(plan.price)}
                                </p>
                                <p className="text-xs text-muted-foreground">/ {plan.billingInterval}</p>
                              </div>
                            </div>
                            {plan.trialDays > 0 && (
                              <p className="text-xs text-green-600 font-medium">{plan.trialDays}-day free trial</p>
                            )}
                            <Button
                              size="sm"
                              className="w-full"
                              onClick={() => handleSubscribe(plan.id)}
                              disabled={subscribing !== null}
                            >
                              {subscribing === plan.id && (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              )}
                              Subscribe
                            </Button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : subscription.status === "pending" ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0" />
                    <div className="flex-1">
                      <p className="font-medium text-yellow-800">Payment setup required</p>
                      <p className="text-sm text-yellow-700 mt-0.5">
                        Complete your payment setup to activate your <strong>{subscription.plan.name}</strong> plan.
                      </p>
                    </div>
                    {subscription.setupUrl && (
                      <Button
                        onClick={() => window.open(subscription.setupUrl, "_blank")}
                        className="shrink-0"
                      >
                        Complete Payment Setup
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ) : subscription.status === "canceled" ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-xl font-semibold">{subscription.plan.name}</h3>
                    <Badge variant="secondary">Cancelled</Badge>
                  </div>
                  {subscription.canceledAt && (
                    <p className="text-sm text-muted-foreground">
                      Cancelled on {new Date(subscription.canceledAt).toLocaleDateString()}
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground">Contact support to reactivate your subscription.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-xl font-semibold">{subscription.plan.name}</h3>
                    {subscription.status === "trialing" && (
                      <Badge className="bg-green-500 hover:bg-green-500 text-white">Free Trial</Badge>
                    )}
                    {subscription.status === "active" && (
                      <Badge className="bg-green-500 hover:bg-green-500 text-white">Active</Badge>
                    )}
                    {subscription.status === "past_due" && (
                      <Badge className="bg-orange-500 hover:bg-orange-500 text-white">Payment Overdue</Badge>
                    )}
                    {subscription.cancelAtPeriodEnd && (
                      <Badge variant="destructive">Cancels at period end</Badge>
                    )}
                  </div>
                  <div className="text-2xl font-bold">
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: subscription.plan.currency.toUpperCase(),
                    }).format(subscription.plan.price)}
                    <span className="text-sm font-normal text-muted-foreground ml-1">
                      / {subscription.plan.billingInterval}
                    </span>
                  </div>
                  {subscription.status === "trialing" && subscription.trialEndsAt && (() => {
                    const daysLeft = Math.ceil((new Date(subscription.trialEndsAt).getTime() - Date.now()) / 86400000);
                    return (
                      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                        <span className="font-medium text-green-700">
                          {daysLeft > 0
                            ? `${daysLeft} day${daysLeft !== 1 ? "s" : ""} remaining in your free trial`
                            : "Your trial has ended"}
                        </span>
                        <span className="text-green-600 text-xs">
                          · Ends {new Date(subscription.trialEndsAt).toLocaleDateString()}
                        </span>
                      </div>
                    );
                  })()}
                  {subscription.status === "past_due" && (
                    <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm">
                      <AlertTriangle className="h-4 w-4 text-orange-600 shrink-0" />
                      <span className="text-orange-700">Your payment is overdue. Please update your payment method.</span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3 text-sm text-muted-foreground">
                    {subscription.currentPeriodEnd && (
                      <div>
                        <span className="font-medium text-foreground">Renews</span>{" "}
                        {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                      </div>
                    )}
                    {subscription.trialEndsAt && new Date(subscription.trialEndsAt) > new Date() && (
                      <div>
                        <span className="font-medium text-foreground">Trial ends</span>{" "}
                        {new Date(subscription.trialEndsAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>

                  {/* Cancel subscription */}
                  {!subscription.cancelAtPeriodEnd && !cancelConfirm && (
                    <div className="pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setCancelConfirm(true); setCancelError(null); }}
                      >
                        Cancel Subscription
                      </Button>
                    </div>
                  )}
                  {cancelConfirm && (
                    <div className="p-4 border border-destructive/30 rounded-lg bg-destructive/5 space-y-3">
                      <p className="text-sm font-medium">Are you sure you want to cancel your subscription?</p>
                      <p className="text-xs text-muted-foreground">This action cannot be undone. You will lose access at the end of the current billing period.</p>
                      {cancelError && <p className="text-xs text-destructive">{cancelError}</p>}
                      <div className="flex gap-2">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={handleCancelSubscription}
                          disabled={canceling}
                        >
                          {canceling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                          Yes, Cancel
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setCancelConfirm(false); setCancelError(null); }}
                          disabled={canceling}
                        >
                          Keep Subscription
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Feature Limits */}
          {!subLoading && subscription && subscription.status !== "pending" && subscription.status !== "canceled" && (
            <PlanAllowances limits={subscription.plan.limits} />
          )}

        </TabsContent>

        <TabsContent value="invoicing" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Invoice History</CardTitle>
              <CardDescription>
                View and download your past invoices.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {invoicesLoading ? (
                <div className="space-y-2">
                  {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} className="h-14 w-full" />
                  ))}
                </div>
              ) : !invoicesData || invoicesData.data.length === 0 ? (
                <div className="text-center py-8 space-y-3">
                  <FileText className="h-10 w-10 mx-auto text-muted-foreground" />
                  <p className="text-muted-foreground text-sm">No invoices yet.</p>
                </div>
              ) : (
                <>
                  <div className="divide-y rounded-lg border">
                    {invoicesData.data.map((invoice) => (
                      <div key={invoice.id} className="flex items-center justify-between px-4 py-3 gap-4">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">
                            {invoice.invoiceNumber ?? invoice.id.slice(0, 8)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(invoice.createdAt).toLocaleDateString()}
                            {invoice.description && ` · ${invoice.description}`}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-semibold text-sm">
                            {formatCurrency(invoice.amount, invoice.currency)}
                          </p>
                          <Badge variant={invoiceStatusVariant(invoice.status)} className="text-xs capitalize mt-0.5">
                            {invoice.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {invoice.status !== "paid" && (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handlePayInvoice(invoice.id)}
                              disabled={payingInvoiceId === invoice.id}
                              title="Pay this invoice"
                            >
                              {payingInvoiceId === invoice.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <CreditCard className="h-4 w-4 mr-1" />
                              )}
                              Pay Now
                            </Button>
                          )}
                          {invoice.pdfUrl && (
                            <Button variant="ghost" size="icon" asChild>
                              <a href={invoice.pdfUrl} target="_blank" rel="noopener noreferrer" title="Download PDF">
                                <Download className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                          {invoice.hostedInvoiceUrl && invoice.status === "paid" && (
                            <Button variant="ghost" size="icon" asChild>
                              <a href={invoice.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer" title="View invoice">
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {payError && (
                    <p className="text-sm text-destructive mt-2">{payError}</p>
                  )}

                  {/* Pagination */}
                  {invoicesData.pagination.totalPages > 1 && (
                    <div className="flex items-center justify-between text-sm">
                      <p className="text-muted-foreground">
                        Page {invoicesData.pagination.page} of {invoicesData.pagination.totalPages}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={invoicePage <= 1}
                          onClick={() => setInvoicePage((p) => p - 1)}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Prev
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={invoicePage >= invoicesData.pagination.totalPages}
                          onClick={() => setInvoicePage((p) => p + 1)}
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}

            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="support" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Support Tickets</CardTitle>
              <CardDescription>
                Create and manage your support requests.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {supportView === "list" && (
                <TicketListView
                  onSelectTicket={(id) => setSupportView({ ticketId: id })}
                  onCreateNew={() => setSupportView("create")}
                />
              )}
              {supportView === "create" && (
                <CreateTicketForm
                  onCancel={() => setSupportView("list")}
                  onCreated={() => setSupportView("list")}
                />
              )}
              {typeof supportView === "object" && "ticketId" in supportView && (
                <TicketDetailView
                  ticketId={supportView.ticketId}
                  onBack={() => setSupportView("list")}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AccountPage;
